import React, { useCallback, useEffect, useState } from "react";
import { Package, ArrowUpRight, ArrowDownLeft, AlertTriangle, Euro, Check, FileText, HandCoins } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { apiError } from "@/lib/api";
import { fmtDate, money } from "@/lib/constants";
import { useLang } from "@/i18n";
import { Panel, Empty } from "@/pages/shared";

const today = () => new Date().toISOString().slice(0, 10);

const ACCENT = {
  indigo: { chip: "bg-indigo-500/15 text-indigo-500 dark:text-indigo-400", bar: "bg-indigo-500" },
  blue: { chip: "bg-blue-500/15 text-blue-500 dark:text-blue-400", bar: "bg-blue-500" },
  amber: { chip: "bg-amber-500/15 text-amber-600 dark:text-amber-400", bar: "bg-amber-500" },
  red: { chip: "bg-rose-500/15 text-rose-500 dark:text-rose-400", bar: "bg-rose-500" },
  green: { chip: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400", bar: "bg-emerald-500" },
};

const Kpi = ({ icon: Icon, label, value, accent }) => (
  <div className="relative overflow-hidden rounded-lg border border-border bg-card p-4" data-testid={`rental-kpi-${label}`}>
    <span className={`absolute left-0 top-0 h-full w-1 ${accent.bar}`} />
    <div className="flex items-center justify-between">
      <span className={`inline-flex items-center justify-center rounded-md p-2 ${accent.chip}`}><Icon className="h-4 w-4" /></span>
      <span className="font-display text-3xl font-extrabold tabular-nums text-foreground" data-testid={`rental-kpi-value-${label}`}>{value}</span>
    </div>
    <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">{label}</p>
  </div>
);

const ListCard = ({ icon: Icon, title, rows, accent, label, action }) => (
  <div className="relative overflow-hidden rounded-lg border border-border bg-card" data-testid={`rental-section-${title}`}>
    <span className={`absolute left-0 top-0 h-full w-1 ${accent.bar}`} />
    <div className="flex items-center gap-2.5 px-4 py-3 border-b border-border">
      <span className={`inline-flex items-center justify-center rounded-md p-1.5 ${accent.chip}`}><Icon className="h-4 w-4" /></span>
      <p className="font-display font-bold text-sm text-foreground">{title}</p>
      <span className="ml-auto inline-flex min-w-[1.5rem] justify-center rounded-full bg-muted px-2 py-0.5 text-xs font-bold tabular-nums text-foreground">{rows.length}</span>
    </div>
    {rows.length === 0 ? (
      <p className="px-4 py-4 text-xs text-muted-foreground">—</p>
    ) : (
      <div className="divide-y divide-border">
        {rows.map((r) => (
          <div key={r.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/50 transition-colors" data-testid={`rental-ov-${r.id}`}>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-medium text-foreground">{r.qty}× {r.names?.nl || r.names?.en || r.code}</p>
              <p className="truncate text-[11px] text-muted-foreground">{r.guest_name} · {r.pitch_label}</p>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
              <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] tabular-nums text-muted-foreground">{fmtDate(r.start)} → {fmtDate(r.returned_on || r.end)}</span>
              {label && <span className="text-[10px] text-muted-foreground">{label(r)}</span>}
            </div>
            {action && <div className="shrink-0">{action(r)}</div>}
          </div>
        ))}
      </div>
    )}
  </div>
);

export default function Verhuur() {
  const { t } = useLang();
  const [day, setDay] = useState(today());
  const [data, setData] = useState(null);

  const load = useCallback(async (d) => {
    try { setData((await api.get(`/rentals/overview?day=${d}`)).data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(day); }, [load, day]);

  const act = async (r, action) => {
    try {
      await api.post(`/rentals/${r.id}/deposit`, { action });
      await load(day); toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openPdf = async (r) => {
    try {
      const { data: blob } = await api.get(`/rentals/${r.id}/deposit-pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const giveBack = async (r) => {
    try {
      const d = day > r.start && day <= r.end ? day : r.end;
      await api.post(`/rentals/${r.id}/return`, { date: d });
      await load(day); toast.success(t("rental_returned"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Panel
      title={t("nav_verhuur")}
      testid="rental-overview-panel"
      action={
        <div className="flex items-end gap-2">
          <div className="space-y-1">
            <Label className="text-xs">{t("date")}</Label>
            <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} data-testid="rental-overview-day" />
          </div>
          <Button variant="outline" size="sm" onClick={() => setDay(today())} data-testid="rental-overview-today">{t("view_today")}</Button>
        </div>
      }
    >
      {!data ? <Empty /> : (
        <div className="p-5 space-y-8">
          {/* Samenvatting: aantallen in één oogopslag */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Kpi icon={ArrowUpRight} label={t("rental_out_today")} value={data.out_today.length} accent={ACCENT.indigo} />
            <Kpi icon={ArrowDownLeft} label={t("rental_back_today")} value={data.back_today.length} accent={ACCENT.amber} />
            <Kpi icon={Package} label={t("rental_out_now")} value={data.out_now.length} accent={ACCENT.blue} />
            <Kpi icon={AlertTriangle} label={t("rental_overdue")} value={data.overdue.length} accent={ACCENT.red} />
          </div>

          {/* Actielijsten */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <ListCard icon={ArrowUpRight} title={t("rental_out_today")} rows={data.out_today} accent={ACCENT.indigo} />
            <ListCard icon={ArrowDownLeft} title={t("rental_back_today")} rows={data.back_today} accent={ACCENT.amber}
              action={(r) => <Button size="sm" variant="outline" className="h-7" onClick={() => giveBack(r)} data-testid={`ov-return-${r.id}`}>{t("rental_return")}</Button>} />
            <ListCard icon={Package} title={t("rental_out_now")} rows={data.out_now} accent={ACCENT.blue} />
            <ListCard icon={AlertTriangle} title={t("rental_overdue")} rows={data.overdue} accent={ACCENT.red}
              action={(r) => <Button size="sm" className="h-7" onClick={() => giveBack(r)} data-testid={`ov-return-late-${r.id}`}>{t("rental_return")}</Button>} />
          </div>

          {/* Waarborgen */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <ListCard icon={HandCoins} title={t("rental_deposit_todo")} rows={data.deposits_todo} accent={ACCENT.amber}
              label={(r) => `${t("rental_deposit")} ${money(r.deposit_total)}`}
              action={(r) => <Button size="sm" className="h-7" onClick={() => act(r, "paid")} data-testid={`deposit-paid-${r.id}`}>{t("deposit_received")}</Button>} />
            <ListCard icon={Euro} title={`${t("rental_deposit_open")} · ${money(data.deposit_open_total)}`} rows={data.deposits_open} accent={ACCENT.green}
              label={(r) => `${t("deposit_received")} ${fmtDate(r.deposit_paid_on)} · ${money(r.deposit_open)}`}
              action={(r) => (
                <span className="flex gap-1">
                  <Button size="sm" variant="ghost" className="h-7" title={t("deposit_receipt")} onClick={() => openPdf(r)} data-testid={`deposit-pdf-${r.id}`}>
                    <FileText className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" className="h-7" onClick={() => act(r, "refunded")} data-testid={`deposit-refund-${r.id}`}>
                    <Check className="h-3.5 w-3.5 mr-1" />{t("deposit_refund")}
                  </Button>
                </span>
              )} />
          </div>

          <RentalReport />
        </div>
      )}
    </Panel>
  );
}

const MONTH_SHORT = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];

/** Per maand: opbrengst en hoe vaak/hoe lang elk artikel uit was. */
const RentalReport = () => {
  const { t } = useLang();
  const [year, setYear] = useState(new Date().getFullYear());
  const [rep, setRep] = useState(null);

  useEffect(() => {
    api.get(`/rentals/report?year=${year}`).then(({ data }) => setRep(data)).catch(() => setRep(null));
  }, [year]);

  const rows = (rep?.items || []).filter((i) => i.total_count > 0 || i.total_revenue > 0);

  return (
    <div className="relative overflow-hidden rounded-lg border border-border bg-card" data-testid="rental-report">
      <span className="absolute left-0 top-0 h-full w-1 bg-primary" />
      <div className="flex items-center gap-2.5 px-4 py-3 border-b border-border">
        <span className="inline-flex items-center justify-center rounded-md bg-primary/15 p-1.5 text-primary"><Euro className="h-4 w-4" /></span>
        <p className="font-display font-bold text-sm text-foreground">{t("rental_report")}</p>
        <span className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setYear(year - 1)} data-testid="report-prev-year">‹</Button>
          <b className="tabular-nums text-sm text-foreground" data-testid="report-year">{year}</b>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setYear(year + 1)} data-testid="report-next-year">›</Button>
          <span className="ml-1 font-display font-extrabold text-primary tabular-nums" data-testid="report-total">{money(rep?.total_revenue || 0)}</span>
        </span>
      </div>
      {rows.length === 0 ? (
        <p className="px-4 py-4 text-xs text-muted-foreground">{t("rental_report_empty")}</p>
      ) : (
        <div className="overflow-auto cb-scroll">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-border bg-muted/50 text-muted-foreground">
                <th className="text-left px-3 py-2 font-bold uppercase tracking-wider text-[10px]">{t("rental_item")}</th>
                {MONTH_SHORT.map((m) => <th key={m} className="px-2 py-2 text-right font-bold tabular-nums">{m}</th>)}
                <th className="px-3 py-2 text-right font-bold uppercase tracking-wider text-[10px]">{t("total")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((i) => (
                <tr key={i.item_id} className="hover:bg-muted/40 transition-colors" data-testid={`report-row-${i.item_id}`}>
                  <td className="px-3 py-2">
                    <b className="text-foreground">{i.code}</b> <span className="text-muted-foreground">{i.name}</span>
                    <span className="block text-[10px] text-muted-foreground">{i.total_count}× {t("rental_times_out")} · {i.total_days} {t("nights")}</span>
                  </td>
                  {i.months.map((m) => (
                    <td key={m.month} className={`px-2 py-2 text-right tabular-nums ${m.revenue ? "text-foreground" : "text-muted-foreground/30"}`}>
                      {m.revenue ? m.revenue.toFixed(0) : "·"}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right tabular-nums font-bold text-primary">{money(i.total_revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
